import { Box, ButtonBase, Tooltip, Typography, useTheme } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import { appThemes, useMeetingAppContext } from "../MeetingAppContextDef";
import useResponsiveSize from "../utils/useResponsiveSize";
import Lottie from "react-lottie";

const OutlineIconTextButton = ({
  onClick,
  isFocused,
  bgColor,
  Icon,
  focusBGColor,
  disabled,
  renderRightComponent,
  fillColor,
  lottieOption,
  tooltipTitle,
  btnID,
  buttonText,
  large,
  isRequestProcessing,
  textColor,
  customBtnWidth,
  customBtnHeight,
}) => {
  const theme = useTheme();
  const [mouseOver, setMouseOver] = useState(false);
  const [mouseDown, setMouseDown] = useState(false);
  const [blinkingState, setBlinkingState] = useState(1);

  const intervalRef = useRef();

  const { animationsEnabled, appTheme } = useMeetingAppContext();

  const iconSize = useResponsiveSize({
    xl: '2.4vh',
    lg: '2.2vh',
    md: '2vh',
    sm: '1.8vh',
    xs: '1.6vh',
  });

  const textSize = useResponsiveSize({
    xl: '1.6vh',
    lg: '1.4vh',
    md: '1.2vh',
    sm: '1vh',
    xs: '0.9vh',
  });

  const startBlinking = () => {
    intervalRef.current = setInterval(() => {
      setBlinkingState((s) => (s === 1 ? 0.4 : 1));
    }, 600);
  };

  const stopBlinking = () => {
    clearInterval(intervalRef.current);

    setBlinkingState(1);
  };

  useEffect(() => {
    if (isRequestProcessing) {
      startBlinking();
    } else {
      stopBlinking();
    }
  }, [isRequestProcessing]);

  useEffect(() => {
    return () => {
      stopBlinking();
    };
  }, []);

  return (
    <Tooltip placement="bottom" title={tooltipTitle || ""}>
      <ButtonBase
        id={btnID}
        onMouseEnter={() => {
          setMouseOver(true);
        }}
        onMouseLeave={() => {
          setMouseOver(false);
        }}
        onMouseDown={() => {
          setMouseDown(true);
        }}
        onMouseUp={() => {
          setMouseDown(false);
        }}
        disabled={disabled}
        onClick={onClick}
        style={{
          padding: "0.8vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: '0.8vh',
          overflow: "hidden",
          backgroundColor: bgColor
            ? bgColor
            : isFocused
            ? focusBGColor || appTheme === appThemes.LIGHT
              ? "#95959E"
              : "#fff"
            : appTheme === appThemes.DARK
            ? theme.palette.darkTheme.main
            : appTheme === appThemes.LIGHT
            ? theme.palette.lightTheme.main
            : theme.palette.background.default,
          border: `0.2vh solid ${
            mouseOver || mouseDown
              ? "transparent"
              : bgColor
              ? bgColor
              : focusBGColor
              ? focusBGColor
              : appTheme === appThemes.LIGHT
              ? theme.palette.lightTheme.outlineColor
              : "#ffffff33"
          }`,
          transition: `all ${200 * (animationsEnabled ? 1 : 0.5)}ms`,
          transitionTimingFunction: "linear",
          cursor: "pointer",
          opacity: blinkingState,
          width: customBtnWidth ? customBtnWidth : null,
          height: customBtnHeight ? customBtnHeight : null,
        }}
      >
        <Box
          style={{
            opacity: disabled ? 0.7 : 1,
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: '0.8vh',
            transform: `scale(${mouseOver ? (mouseDown ? 0.97 : 1.05) : 1})`,
            transition: `all ${200 * (animationsEnabled ? 1 : 0.5)}ms`,
            transitionTimingFunction: "linear",
          }}
        >
          {buttonText ? (
            lottieOption ? (
              <Box
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Lottie
                  style={{
                    height: lottieOption?.iconHeight
                      ? lottieOption?.iconHeight
                      : iconSize,
                    // border: `${2}px solid black`,
                    transform: lottieOption?.iconScale
                      ? lottieOption?.iconScale
                      : null,
                    background: `${
                      isFocused
                        ? appTheme === appThemes.LIGHT
                          ? "#95959E"
                          : "#fff"
                        : null
                    }`,
                  }}
                  options={lottieOption}
                  eventListeners={[{ eventName: "done" }]}
                  height={iconSize}
                  width={
                    (iconSize * lottieOption?.width) / lottieOption?.height
                  }
                  isClickToPauseDisabled
                />
              </Box>
            ) : (
              <Typography
                variant="subtitle2"
                style={{
                  fontWeight: "bold",
                  fontSize: textSize,
                  color: isFocused
                    ? appTheme === appThemes.LIGHT
                      ? theme.palette.common.white
                      : "#1C1F2E"
                    : textColor
                    ? textColor
                    : appTheme === appThemes.LIGHT
                    ? "#95959E"
                    : "#fff",
                }}
              >
                {buttonText}
              </Typography>
            )
          ) : null}
        </Box>

        {typeof renderRightComponent === "function" && renderRightComponent()}
      </ButtonBase>
    </Tooltip>
  );
};

export default OutlineIconTextButton;
